/**
 * Template body conversion: Markdown → HTML and plain text → HTML.
 *
 * Pure functions with no Node I/O — safe for use in browser and script contexts.
 * Template variables like {{client.name}} are preserved through conversion.
 */
import { marked } from 'marked';

/**
 * Convert Markdown to sanitized HTML.
 *
 * Template variables {{...}} are temporarily replaced with placeholders
 * to prevent the Markdown parser from mangling them.
 */
export function markdownToHtml(markdown: string): string {
  const variables: string[] = [];
  const placeholderPrefix = '\x00VAR';

  const withPlaceholders = markdown.replace(/\{\{([^}]+)\}\}/g, (_match, varName) => {
    const idx = variables.length;
    variables.push(varName.trim());
    return `${placeholderPrefix}${idx}\x00`;
  });

  const html = marked.parse(withPlaceholders, { async: false }) as string;

  let result = html;
  for (let i = 0; i < variables.length; i++) {
    const placeholder = `${placeholderPrefix}${i}\x00`;
    result = result.replace(placeholder, `{{${variables[i]}}}`);
  }

  return sanitizeHtml(result);
}

/**
 * Convert plain text to HTML paragraphs.
 * Double newlines become paragraph breaks; single newlines become <br>.
 */
export function textToHtml(text: string): string {
  const paragraphs = text.split(/\n{2,}/);
  const html = paragraphs
    .map((p) => {
      const escaped = escapeHtmlPreservingVars(p.trim());
      const withBreaks = escaped.replace(/\n/g, '<br>');
      return `<p>${withBreaks}</p>`;
    })
    .filter((p) => p !== '<p></p>')
    .join('\n');

  return html;
}

/**
 * Escape HTML special characters while preserving template variables.
 */
function escapeHtmlPreservingVars(text: string): string {
  const variables: string[] = [];
  const placeholderPrefix = '\x00VAR';

  const withPlaceholders = text.replace(/\{\{([^}]+)\}\}/g, (_match, varName) => {
    const idx = variables.length;
    variables.push(varName.trim());
    return `${placeholderPrefix}${idx}\x00`;
  });

  const escaped = withPlaceholders
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  let result = escaped;
  for (let i = 0; i < variables.length; i++) {
    const placeholder = `${placeholderPrefix}${i}\x00`;
    result = result.replace(placeholder, `{{${variables[i]}}}`);
  }

  return result;
}

/**
 * Minimal HTML sanitization: strip <script>, <style>, on* attributes.
 * Preserves all structural/formatting HTML that Tiptap can render.
 */
export function sanitizeHtml(html: string): string {
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    .replace(/\s+on\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\s+on\w+\s*=\s*'[^']*'/gi, '')
    .trim();
}

/**
 * Extract all template variable references from text.
 */
export function extractVariables(text: string): string[] {
  const matches = text.matchAll(/\{\{([^}]+)\}\}/g);
  const vars = new Set<string>();
  for (const match of matches) {
    vars.add(match[1].trim());
  }
  return Array.from(vars);
}
